import { useContext, useState } from 'react';
import Dialog from '@/components/Dialog';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import FormattedMessageWithChallengeNaming from '@/components/FormattedMessageWithChallengeNaming';
import ChallengeContext from '../../ChallengeContext';
import ChallengePortfolioContext from '../../../../../ChallengePortfolioContext';
import RowContext from '@/components/ChallengePortfolio/NormalView/Rows/Row/RowContext';
import useApi from '@/lib/api/useApi';
import NewlySharedContext from '@/components/ChallengePortfolio/NormalView/NewlySharedChallenges/NewlySharedContext';
import { ListItemIcon, ListItemText, type MenuItemProps } from '@mui/material';
import DeleteMenuItem from '@/components/Menu/DeleteMenuItem';
import MenuItemLoadingIndicator from '@/components/Menu/MenuItemLoadingIndicator';

type DeleteChallengeProps = Omit<
  MenuItemProps,
  'onClick' | 'disabled' | 'children'
>;

const DeleteChallenge = (props: DeleteChallengeProps) => {
  const api = useApi();

  const [isNewlyShared, , , removeChallengeFromNewlyShared] =
    useContext(NewlySharedContext);
  const [, setChallengePortfolio] = useContext(ChallengePortfolioContext);
  const row = useContext(RowContext);
  const [challenge] = useContext(ChallengeContext);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteChallenge = async (): Promise<void> => {
    setConfirmationDialogOpen(false);

    setDeleting(true);

    await api.delete(`challenge-portfolio/challenges/${challenge.id}`);

    setDeleting(false);
    setConfirmationDialogOpen(false);

    setChallengePortfolio(challengePortfolio => {
      delete challengePortfolio.challenges[challenge.id];

      if (isNewlyShared) {
        removeChallengeFromNewlyShared(challenge.id);

        return;
      }

      challengePortfolio.rows[row.id].challenges.splice(
        challengePortfolio.rows[row.id].challenges.findIndex(
          id => id === challenge.id,
        ),
        1,
      );
    });
  };

  const totalChallengeUsersCount =
    challenge.users.length + challenge.invitees.length;
  const challengeIsShared = totalChallengeUsersCount > 1;

  return (
    <>
      <DeleteMenuItem
        onClick={() => setConfirmationDialogOpen(true)}
        disabled={challengeIsShared || deleting}
        {...props}
      >
        <ListItemIcon>
          {deleting ? (
            <MenuItemLoadingIndicator color="error" />
          ) : (
            <DeleteIcon color="error" />
          )}
        </ListItemIcon>
        <ListItemText>
          <FormattedMessageWithChallengeNaming id="portfolio.challenge.options.delete.label" />
        </ListItemText>
      </DeleteMenuItem>
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        dangerous
      >
        <Dialog.Title>
          <FormattedMessage id="portfolio.challenge.options.delete.dialog.title" />
        </Dialog.Title>
        <Dialog.Content>
          <Dialog.ContentText>
            <FormattedMessageWithChallengeNaming id="portfolio.challenge.options.delete.dialog.text" />
          </Dialog.ContentText>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.Actions.CancelButton
            onClick={() => setConfirmationDialogOpen(false)}
            disabled={deleting}
          />
          <Dialog.Actions.DeleteButton
            onClick={deleteChallenge}
            disabled={deleting}
          />
        </Dialog.Actions>
      </Dialog>
    </>
  );
};

export default DeleteChallenge;
